import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { ShieldCheck, AlertTriangle } from "lucide-react";
import { useSafetyStore } from "@/store/useSafetyStore";
import { useConnectionsStore } from "@/store/useConnectionsStore";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

function pacingZone(pct) {
  if (pct >= 90) return { label: "Near platform limit", cls: "text-rose-700" };
  if (pct >= 70) return { label: "Approaching limit", cls: "text-amber-700" };
  return { label: "Safe zone", cls: "text-emerald-700" };
}

function warmupStepCount(preset) {
  if (preset === "conservative") return 21;
  if (preset === "aggressive") return 7;
  return 14;
}

function warmupCurveExponent(preset) {
  if (preset === "aggressive") return 0.7;
  if (preset === "conservative") return 1.4;
  return 1;
}

export function PacingSafetyTab() {
  const { caps, jitterEnabled, quietHoursStart, quietHoursEnd, warmupPreset, tosAccepted,
    setCap, setJitter, setQuietHours, setWarmupPreset, setTosAccepted } = useSafetyStore();
  const setKeyCap = (key) => (v) => setCap(key, v[0]);
  const getLabel = useConnectionsStore((s) => s.getAccountLabel);
  const resolveLabel = (channelId, fallback) => {
    const r = getLabel(channelId);
    if (!r) return { label: fallback, connected: false };
    return { label: r.label || fallback, connected: r.connected };
  };
  const waL = resolveLabel("whatsapp", "WhatsApp");
  const liL = resolveLabel("linkedin", "LinkedIn");
  const gmL = resolveLabel("gmail", "Gmail");
  const tgL = resolveLabel("telegram", "Telegram");

  const sliderRow = (key, label, hint, testId) => {
    const entry = caps[key];
    const platformLimit = entry.platformLimit;
    const pct = (entry.cap / platformLimit) * 100;
    const zone = pacingZone(pct);
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3.5" data-testid={testId}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="text-[12px] font-semibold text-slate-900 break-words">{label}</div>
            <div className="text-[10.5px] text-slate-500">{hint}</div>
          </div>
          <div className="text-left sm:text-right shrink-0">
            <div className="text-[15px] font-bold tabular-nums text-slate-900">{entry.cap}</div>
            <div className={cn("text-[10px] font-medium", zone.cls)}>{zone.label}</div>
          </div>
        </div>
        <Slider value={[entry.cap]} onValueChange={setKeyCap(key)} min={5} max={platformLimit} step={1} className="mt-3" />
      </div>
    );
  };

  const warmupSteps = warmupStepCount(warmupPreset);
  const warmupCurve = Array.from({ length: warmupSteps }).map((_, i) => ({
    day: i + 1,
    sends: Math.round(5 + (caps.whatsapp.cap - 5) * Math.pow(
      i / Math.max(1, warmupSteps - 1),
      warmupCurveExponent(warmupPreset),
    )),
  }));

  return (
    <div className="p-4 sm:p-6 max-w-4xl space-y-5 min-w-0" data-testid="pacing-safety">
      <div className="rounded-xl border border-emerald-200 bg-gradient-to-br from-emerald-50/60 to-white p-4 flex items-start gap-3" data-testid="ban-risk-banner">
        <div className="h-9 w-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0"><ShieldCheck size={16} /></div>
        <div className="flex-1 min-w-0">
          <div className="text-[13.5px] font-semibold text-slate-900">Account pacing</div>
          <div className="text-[11.5px] text-slate-600 mt-0.5">Configure daily send caps per paired channel account.</div>
        </div>
      </div>

      <section>
        <h3 className="text-[13px] font-semibold text-slate-900 mb-2">Daily caps per paired account</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {sliderRow("whatsapp", waL.connected ? waL.label : `${waL.label} (not paired)`, "Messages per day", "cap-whatsapp")}
          {sliderRow("linkedin", liL.connected ? `${liL.label} — Connects` : `${liL.label} (not paired)`, "Connect requests per day", "cap-li-connects")}
          {sliderRow("linkedinDms", liL.connected ? `${liL.label} — DMs` : `${liL.label} (not paired)`, "Messages per day", "cap-li-dms")}
          {sliderRow("gmail", gmL.connected ? gmL.label : `${gmL.label} (not paired)`, "Sends per day", "cap-gmail")}
          {sliderRow("telegram", tgL.connected ? tgL.label : `${tgL.label} (not paired)`, "Messages per day", "cap-telegram")}
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="jitter-toggle">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[12.5px] font-semibold text-slate-900">Humanlike send-time jitter</div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">Vary send times naturally.</div>
            </div>
            <Switch checked={jitterEnabled} onCheckedChange={setJitter} />
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="quiet-hours">
          <div className="text-[12.5px] font-semibold text-slate-900">Quiet hours</div>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-[12px]">
            <input type="time" value={quietHoursStart} onChange={(e) => setQuietHours(e.target.value, quietHoursEnd)} className="h-10 sm:h-8 rounded border border-slate-200 px-2 text-[12px] min-w-0" />
            <span className="text-slate-500">to</span>
            <input type="time" value={quietHoursEnd} onChange={(e) => setQuietHours(quietHoursStart, e.target.value)} className="h-10 sm:h-8 rounded border border-slate-200 px-2 text-[12px] min-w-0" />
          </div>
        </div>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4" data-testid="warmup-curve">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
          <div className="text-[12.5px] font-semibold text-slate-900">Warmup curve</div>
          <div className="flex items-center gap-1 flex-wrap">
            {["conservative", "standard", "aggressive"].map((p) => (
              <button key={p} type="button" onClick={() => setWarmupPreset(p)} data-testid={`warmup-${p}`} className={cn("text-[10.5px] font-medium px-2 py-1.5 sm:py-1 rounded-full capitalize min-h-8 sm:min-h-0", warmupPreset === p ? "bg-indigo-600 text-white" : "bg-white border border-slate-200 text-slate-600")}>{p}</button>
            ))}
          </div>
        </div>
        <div className="w-full min-w-0" style={{ height: 120 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={warmupCurve} margin={{ top: 4, right: 4, left: -16, bottom: 0 }}>
              <CartesianGrid stroke="#F1F5F9" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
              <YAxis width={28} tick={{ fontSize: 9 }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Line type="monotone" dataKey="sends" stroke="#6366F1" strokeWidth={2} dot={{ r: 2.5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className={cn("rounded-lg border-2 p-4", tosAccepted ? "border-slate-200 bg-slate-50/60" : "border-rose-200 bg-rose-50/40")} data-testid="tos-banner">
        <div className="flex items-start gap-3">
          <AlertTriangle size={15} className={tosAccepted ? "text-slate-400 mt-0.5" : "text-rose-500 mt-0.5"} />
          <div className="flex-1">
            <div className="text-[12.5px] font-semibold text-slate-900">Terms-of-service acknowledgment</div>
            <label className="mt-3 inline-flex items-center gap-2 cursor-pointer text-[12px] font-medium text-slate-800">
              <input type="checkbox" checked={tosAccepted} onChange={(e) => setTosAccepted(e.target.checked)} className="h-3.5 w-3.5 accent-indigo-600" data-testid="tos-ack-checkbox" />
              <span>I understand and accept this risk.</span>
            </label>
          </div>
        </div>
      </section>
    </div>
  );
}
